import { Alert, Button, Chip, IconButton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import React, { Dispatch, FC, SetStateAction, useContext } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import AddNewTagModal from '../AddNewTagModal/AddNewTagModal';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import DeleteTagModal from '../DeleteTagModal/DeleteTagModal';
import { useGetTags } from '@features/applications/hooks/useGetTags';
import { useAssignTags } from '@features/applications/hooks/useAssignTags';
import { toast } from 'react-toastify';
import { lightGrey, lightGrey2 } from '@styles/colors';
import { authContext } from '@staffedupusa/shared-components';
import { Application } from '@features/applications/types';

type AssignTagsModalProps = {
  selectedApplicationsIds: number[] | undefined
  handleClose: () => void;
  handlePageNo?: () => void;
  setApplicationData?: Dispatch<SetStateAction<Application[] | null>>;
  setApplication?: Dispatch<SetStateAction<Application | null>>;
  selectedCompanyId?: string | number;
};

type Tag = {
  id: number;
  tag: string;
} | null;

const AssignTagsModal: FC<AssignTagsModalProps> = ({ selectedApplicationsIds, selectedCompanyId, handleClose, handlePageNo, setApplicationData, setApplication }) => {
  const { authData } = useContext(authContext);
  const [selectedTags, setSelectedTags] = React.useState<number[]>([]);
  const [showNewTag, setShowNewTag] = React.useState<boolean>(false);
  const [editableTag, setEditableTag] = React.useState<Tag>(null);
  const [deleteTag, setDeleteTag] = React.useState<Tag>(null);
  const [errorText, setErrorText] = React.useState<string>('');

  const { tagsData, isLoadingTags } = useGetTags({ selectedCompanyId, setErrorText, enabled: authData?.user?.enterprise ? true : false });

  const { assignTags, isAssignTagsLoading } = useAssignTags({ setErrorText });

  const handleAddNewTag = () => {
    setShowNewTag(true);
  };

  const handleEditTag = (tag: { tag: string, id: number }) => {
    setEditableTag(tag);
  };

  const handleDeleteTag = (tag: { tag: string, id: number }) => {
    setDeleteTag(tag);
  };

  const handleSelectTag = (tag: number) => {
    setSelectedTags((prevSelectedTags) =>
      prevSelectedTags.includes(tag)
        ? prevSelectedTags.filter((t) => t !== tag)
        : [...prevSelectedTags, tag]
    );
  };

  const handleAssignTags = () => {
    const options = {
      onSuccess: (data: { message: string, applications: Application[] }) => {
        const updatedSelectedApplication = data?.applications.find(
          (application: { id: any }) => application.id === application?.id,
        );
        if (updatedSelectedApplication && setApplication) {
          setApplication(updatedSelectedApplication);
        }

        setApplicationData && updatedSelectedApplication && setApplicationData((prev) => {
          if (!prev) return null;
          return prev?.map((application) => {
            if (application.id === updatedSelectedApplication.id) {
              return updatedSelectedApplication;
            }
            return application;
          });
        });
        toast.success(data.message)
        handlePageNo && handlePageNo();
        handleClose()
      },
    };

    const tags = selectedTags;
    const application_id = selectedApplicationsIds ?? [];

    assignTags({ tags, application_id, company_id: selectedCompanyId }, options);
  };

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        variant="contained"
        color="primary"
        onClick={handleAddNewTag}>
        Add New
      </Button>

      <Typography textAlign='center' >Select From Following Tags</Typography>
      {errorText && <Alert severity="error">{errorText}</Alert>}
      <Stack sx={{ maxHeight: "250px", overflow: "auto" }}>
        {isLoadingTags ? (
          <Typography>Loading Tags...</Typography>
        ) : (
          <>
            {tagsData && tagsData.objects?.length > 0 && tagsData.objects.map((item: { id: number; tag: string; predefined?: any; }, index: number) => (
              <Stack
                key={item.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ margin: '3px !important' }}
              >
                <Chip
                  label={item.tag}
                  icon={selectedTags.includes(item.id) ? <CheckIcon color="inherit" /> : undefined}
                  sx={{ color: 'black', backgroundColor: lightGrey, padding: '5px 10px' }}
                  onClick={() => handleSelectTag(item.id)}
                />
                <Stack direction="row">
                  <IconButton disabled={item.predefined === 1} onClick={() => handleEditTag(item)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton disabled={item.predefined === 1} onClick={() => handleDeleteTag(item)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            ))}
          </>
        )}
      </Stack>

      <Button
        startIcon={<DoneIcon />}
        variant="contained"
        color="success"
        disabled={selectedTags.length === 0 || isAssignTagsLoading}
        onClick={handleAssignTags}
      >
        {isAssignTagsLoading ? 'Loading...' : 'Save'}
      </Button>

      {(showNewTag || editableTag) &&
        <AddNewTagModal
          handleClose={() => {
            setShowNewTag(false)
            setEditableTag(null)
          }}
          editable={editableTag ?? null}
          selectedCompanyId={selectedCompanyId}
        />
      }

      {deleteTag &&
        <DeleteTagModal
          handleClose={() => setDeleteTag(null)}
          deleteTag={deleteTag}
          selectedCompanyId={selectedCompanyId}
        />
      }
    </>
  );
};

export default AssignTagsModal;
